import { useCallback } from 'react';
import { filterPdfFiles, createDocumentEntry } from '../utils/fileUtils';
import { apiFetch } from '../../../auth/apiFetch';

export const useDocumentUpload = (apiBaseUrl, setDocuments, validateAccessibility) => {
  const processPdfFiles = useCallback(async (pdfFiles) => {
    if (pdfFiles.length === 0) return;

    // Direct multipart upload — used as local dev fallback (nginx allows up to 100MB)
    const directUpload = async (files) => {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const url = new URL(`${apiBaseUrl}/api/classify`);
      url.searchParams.append('validate_accessibility', validateAccessibility.toString());
      const response = await apiFetch(url.toString(), { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Failed to process PDFs');
      return await response.json(); // List[ClassificationResponse]
    };

    // Presigned S3 upload for a single file, then classify from S3 (async job + polling)
    const uploadAndClassify = async (file, initData) => {
      const s3Response = await fetch(initData.presigned_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/pdf' },
        body: file,
      });
      if (!s3Response.ok) throw new Error(`S3 upload failed: HTTP ${s3Response.status}`);

      // Start async classification — returns immediately with a job_id
      const startResponse = await apiFetch(`${apiBaseUrl}/api/classify-s3`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          s3_key: initData.s3_key,
          filename: file.name,
          validate_accessibility: initData.validate_accessibility,
        }),
      });
      if (!startResponse.ok) throw new Error('Failed to start classification');
      const { job_id } = await startResponse.json();

      // Show classifying spinner while the backend processes the PDF
      setDocuments((prev) =>
        prev.map((doc) => doc.filename === file.name ? { ...doc, status: 'classifying' } : doc)
      );

      // Poll every 2 seconds until the job completes or fails
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const statusResponse = await apiFetch(`${apiBaseUrl}/api/classify-s3/status/${job_id}`);
        if (!statusResponse.ok) throw new Error('Failed to check classification status');
        const job = await statusResponse.json();
        if (job.status === 'completed') return job.result;
        if (job.status === 'failed') throw new Error(job.error || 'Classification failed');
        // status === 'running' → keep polling
      }
    };

    try {
      // Probe whether presigned S3 upload is available (returns 501 when USE_S3=false)
      const probeResponse = await apiFetch(`${apiBaseUrl}/api/pdf/upload-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: pdfFiles[0].name, validate_accessibility: validateAccessibility }),
      });

      if (probeResponse.status === 501) {
        // S3 not available (local dev) — fall back to direct multipart upload
        const results = await directUpload(pdfFiles);
        setDocuments((prev) =>
          prev.map((doc) => {
            const match = results.find((r) => r.filename === doc.filename);
            return match ? { ...doc, status: 'ready', classificationData: match } : doc;
          })
        );
        return;
      }

      if (!probeResponse.ok) throw new Error('Failed to initiate PDF upload');

      // Process all files via presigned URL flow
      const results = [];
      const firstInitData = await probeResponse.json();
      results.push(await uploadAndClassify(pdfFiles[0], firstInitData));

      for (const file of pdfFiles.slice(1)) {
        const initResponse = await apiFetch(`${apiBaseUrl}/api/pdf/upload-init`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, validate_accessibility: validateAccessibility }),
        });
        if (!initResponse.ok) throw new Error('Failed to initiate PDF upload');
        const initData = await initResponse.json();
        results.push(await uploadAndClassify(file, initData));
      }

      setDocuments((prev) =>
        prev.map((doc) => {
          const match = results.find((r) => r.filename === doc.filename);
          return match ? { ...doc, status: 'ready', classificationData: match } : doc;
        })
      );
    } catch (err) {
      console.error(err);
      setDocuments((prev) =>
        prev.map((doc) =>
          pdfFiles.some((f) => f.name === doc.filename)
            ? { ...doc, status: 'error' }
            : doc
        )
      );
    }
  }, [apiBaseUrl, setDocuments, validateAccessibility]);

  const handleMultipleUploads = useCallback(async (files) => {
    const newDocs = files.map(createDocumentEntry);
    setDocuments((prev) => [...prev, ...newDocs]);
    const pdfFiles = filterPdfFiles(files);
    await processPdfFiles(pdfFiles);
  }, [processPdfFiles, setDocuments]);

  return { handleMultipleUploads };
};
